// ChartSettings.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Switch, 
  FormControlLabel, 
  Divider, 
  CircularProgress,
  Slider,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { combinedChildKeys } from '../../utils/chartTheme';

// This dialog edits PRESENTATION only: how a chart is drawn. Its title and
// axis labels are content, written by the logic block that produced it, and
// are not offered here — an override of those would outlive a rewrite of the
// analysis and put the old words on the new chart.
const DEFAULT_CHART_SETTINGS = {
  show_grid: true,
  show_legend: true,
  // Upright. Tilted tick labels are harder to read and were never asked for by
  // the data — a rotation is now something a user turns on, not lives with.
  x_axis_rotation: 0,
  show_points: true,
  line_width: 2,
  bar_width: 0.6,
  horizontal: false,
  stacked: false,
};

// The settings each chart type can honour — nothing else is shown or saved for
// it. A pie has no axes or grid; a box chart names each box on its axis and
// never draws a legend; a combined chart has no drawing of its own.
const TYPE_SETTINGS = {
  bar: ['grid', 'show_legend', 'x_axis_rotation', 'horizontal', 'stacked', 'bar_width'],
  line: ['grid', 'show_legend', 'x_axis_rotation', 'show_points', 'line_width'],
  scatter: ['grid', 'show_legend', 'x_axis_rotation'],
  pie: ['show_legend'],
  box_and_whisker: ['grid', 'x_axis_rotation'],
  superchart: [],
};

const isPlainObject = value => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const ROOT_CHART_PATH = 'root';
const CHART_TYPE_LABELS = {
  bar: 'Bar',
  line: 'Line',
  scatter: 'Scatter',
  pie: 'Pie',
  box_and_whisker: 'Box and Whisker',
  superchart: 'Combined Chart',
};

const settingsFromChart = (chart, storedSettings = {}) => {
  const storedDisplaySettings = isPlainObject(storedSettings) ? { ...storedSettings } : {};
  delete storedDisplaySettings.children;
  delete storedDisplaySettings.children_by_key;
  const effective = {
    ...(isPlainObject(chart) ? chart : {}),
    ...storedDisplaySettings,
  };
  return {
    ...DEFAULT_CHART_SETTINGS,
    show_grid: effective.grid !== false,
    show_legend: effective.show_legend !== false,
    x_axis_rotation: effective.x_axis_rotation ?? 0,
    show_points: effective.show_points !== false,
    line_width: effective.line_width ?? 2,
    bar_width: effective.bar_width ?? 0.6,
    horizontal: effective.horizontal === true,
    stacked: effective.stacked === true,
  };
};

// A SuperChart's settings mirror its element tree. The form addresses panels
// by position (`path`, for the tabs), but STORAGE addresses each child by its
// stable key (`children_by_key`, see combinedChildKeys) so a saved setting
// follows the chart it was made for when a later run reorders the children.
// Leaf values use the same flat shape as an ordinary chart's settings. An
// older save's positional `children` is ignored: it is exactly the shape that
// put one child's settings on another.
export const buildSettingsByPath = (
  chart,
  storedSettings = {},
  path = ROOT_CHART_PATH,
  result = {},
) => {
  if (!isPlainObject(chart)) return result;
  const storedNode = isPlainObject(storedSettings) ? storedSettings : {};
  result[path] = settingsFromChart(chart, storedNode);
  if (chart.type === 'superchart') {
    const storedChildren = isPlainObject(storedNode.children_by_key) ? storedNode.children_by_key : {};
    const keys = combinedChildKeys(chart.elements);
    (chart.elements || []).forEach((child, index) => {
      const childPath = path === ROOT_CHART_PATH ? String(index) : `${path}.${index}`;
      buildSettingsByPath(child, storedChildren[keys[index]], childPath, result);
    });
  }
  return result;
};

// The panels with settings to edit: every leaf chart, in reading order. A
// combined chart is a container and has none of its own.
const editablePanels = (chart) => flattenChartPanels(chart).filter((panel) => panel.chart.type !== 'superchart');

const flattenChartPanels = (chart, path = ROOT_CHART_PATH, result = []) => {
  if (!isPlainObject(chart)) return result;
  result.push({ path, chart });
  if (chart.type === 'superchart') {
    (chart.elements || []).forEach((child, index) => {
      const childPath = path === ROOT_CHART_PATH ? String(index) : `${path}.${index}`;
      flattenChartPanels(child, childPath, result);
    });
  }
  return result;
};

// Labelled by the chart's own title — the logic block's words.
// Two children with the same title and type are told apart by position
// ("Premium · Bar 1", "Premium · Bar 2") rather than left identical.
const panelLabel = ({ path, chart }, duplicate = false) => {
  const typeLabel = CHART_TYPE_LABELS[chart.type] || 'Chart';
  const title = String(chart.title ?? '').trim();
  const displayPath = path
    .split('.')
    .map(part => Number(part) + 1)
    .join('.');
  if (!title) return `${typeLabel} ${displayPath}`;
  return duplicate ? `${title} · ${typeLabel} ${displayPath}` : `${title} · ${typeLabel}`;
};

// The form's values in the stored shape, keeping only what this chart type
// honours. `show_grid` on the form is `grid` in storage.
const STORED_KEY = { show_grid: 'grid' };
const sanitizeChartSettings = (settings, chartType) => {
  const allowed = TYPE_SETTINGS[chartType] || [];
  const stored = {};
  Object.entries(settings || {}).forEach(([formKey, value]) => {
    const key = STORED_KEY[formKey] || formKey;
    if (allowed.includes(key) && value !== undefined) stored[key] = value;
  });
  return stored;
};

// Two settings values are the same when every value matches; key order and
// object identity do not count.
const sameSettings = (a, b) => {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b)
      && a.length === b.length
      && a.every((item, index) => sameSettings(item, b[index]));
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  return keysA.length === keysB.length && keysA.every((key) => sameSettings(a[key], b[key]));
};

// Only DELIBERATE differences are saved. The chart the run produced is the
// baseline; a form value equal to it is not stored, so that field keeps
// following whatever each run produces — a later run that changes it is not
// overruled by a value nobody chose. A value the user moved away from the
// baseline is stored and wins.
export const sparseChartSettings = (chart, formValues) => {
  const wanted = sanitizeChartSettings(formValues, chart.type);
  const baseline = sanitizeChartSettings(settingsFromChart(chart, {}), chart.type);
  return Object.fromEntries(
    Object.entries(wanted).filter(([key, value]) => !sameSettings(value, baseline[key])),
  );
};

// The tree to store: one sparse node per chart, a combined chart carrying its
// children by stable key. Children with nothing to say are left out.
export const buildSettingsTree = (chart, settingsByPath, path = ROOT_CHART_PATH) => {
  const node = sparseChartSettings(chart, settingsByPath[path] || settingsFromChart(chart));
  if (chart.type === 'superchart') {
    const children = {};
    const keys = combinedChildKeys(chart.elements);
    (chart.elements || []).forEach((child, index) => {
      const childPath = path === ROOT_CHART_PATH ? String(index) : `${path}.${index}`;
      const childNode = buildSettingsTree(child, settingsByPath, childPath);
      if (Object.keys(childNode).length > 0) children[keys[index]] = childNode;
    });
    if (Object.keys(children).length > 0) node.children_by_key = children;
  }
  return node;
};

// What the stored settings for this entry actually DO to this chart, in the
// same sparse shape — the honoured keys that differ from the chart's own
// values. An older save that stored every field, or a title, compares equal
// to a form that changed nothing, so a Save with no change writes nothing.
const effectiveStoredTree = (stored, chart) => (
  buildSettingsTree(chart, buildSettingsByPath(chart, isPlainObject(stored) ? stored : {}))
);

const storedSettingsTree = (allSettings, sourceBlockId, entryKey, chart) => {
  const stored = isPlainObject(allSettings)
    && isPlainObject(allSettings[sourceBlockId])
    && isPlainObject(allSettings[sourceBlockId][entryKey])
    ? allSettings[sourceBlockId][entryKey]
    : null;
  return effectiveStoredTree(stored, chart);
};

export const mergeChartSettingsMap = (storedSettings, sourceBlockId, entryKey, entrySettings) => {
  const chartSettings = isPlainObject(storedSettings) ? storedSettings : {};
  const sourceSettings = isPlainObject(chartSettings[sourceBlockId])
    ? chartSettings[sourceBlockId]
    : {};
  return {
    ...chartSettings,
    [sourceBlockId]: {
      ...sourceSettings,
      [entryKey]: entrySettings,
    },
  };
};

function ChartSettings({
  open,
  onClose,
  functionId,
  sourceBlockId,
  entryKey,
  chartSpec,
  companyName,
  // Chart settings are live DISPLAY configuration on the function head — how
  // its charts are drawn, for every run of any version, on every screen that
  // shows them. They are not part of the executable version: a save is a plain
  // write that needs no publish and does not mark the function as changed.
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // `error` is a LOAD failure: there is no form to show. `saveError` is a
  // failed Save: the form and its values stay on screen beside the message,
  // and Save is the retry.
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [settingsByPath, setSettingsByPath] = useState({});
  const [activeChartPath, setActiveChartPath] = useState(ROOT_CHART_PATH);

  // Load chart data and settings from Firestore. Everything on screen belongs
  // to ONE target (function / block / entry): the previous target's chart,
  // settings and error are cleared before the read, and a read that completes
  // after the target changed or the dialog closed writes nothing — otherwise
  // chart A's settings could be shown, and saved, under chart B.
  useEffect(() => {
    setChartData(null);
    setSettingsByPath({});
    setActiveChartPath(ROOT_CHART_PATH);
    setError('');
    setSaveError('');
    if (!open || !companyName || !functionId || !sourceBlockId || !entryKey) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const docRef = doc(db, `users/${companyName}/data_functions`, functionId);
        const docSnap = await getDoc(docRef);
        if (cancelled) return;

        if (!docSnap.exists()) {
          setError('Function not found');
          return;
        }

        const allSettings = docSnap.data()?.chart_settings;
        const storedChart = isPlainObject(allSettings)
          && isPlainObject(allSettings[sourceBlockId])
          && isPlainObject(allSettings[sourceBlockId][entryKey])
          ? allSettings[sourceBlockId][entryKey]
          : {};
        const chart = isPlainObject(chartSpec) ? chartSpec : {};
        setChartData(chart);
        setSettingsByPath(buildSettingsByPath(chart, storedChart));
        // A combined chart has no settings of its own: open on its first chart.
        setActiveChartPath(editablePanels(chart)[0]?.path ?? ROOT_CHART_PATH);
      } catch (err) {
        console.error("Error loading chart data:", err);
        if (!cancelled) setError('Failed to load chart settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyName, functionId, sourceBlockId, entryKey, chartSpec]);

  // Handle saving updated chart settings
  const handleSave = async () => {
    if (!companyName || !functionId || !sourceBlockId || !entryKey || !chartData) {
      setSaveError('Missing required data to save settings');
      return;
    }

    setSaving(true);
    setSaveError('');

    try {
      const sanitized = buildSettingsTree(chartData, settingsByPath);

      // One transaction: read the map as it is at commit time, merge this one
      // entry over it, write. A dialog saving a sibling chart at the same
      // moment cannot be overwritten — the transaction re-runs on the fresh
      // document — and a Save that changes nothing writes nothing.
      const functionRef = doc(db, `users/${companyName}/data_functions`, functionId);
      const written = await runTransaction(db, async (tx) => {
        const snapshot = await tx.get(functionRef);
        if (!snapshot.exists()) throw new Error('Function not found');
        const allSettings = snapshot.data()?.chart_settings;
        if (sameSettings(sanitized, storedSettingsTree(allSettings, sourceBlockId, entryKey, chartData))) {
          return false;
        }
        tx.update(functionRef, {
          chart_settings: mergeChartSettingsMap(allSettings, sourceBlockId, entryKey, sanitized),
        });
        return true;
      });

      // Every surface drawing this chart follows the head, so the new look is
      // already on screen by the time the dialog closes.
      onClose(written);
    } catch (err) {
      console.error("Error saving chart settings:", err);
      setSaveError('Failed to save chart settings. Your changes are still here — try again.');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults: the chart as the function produced it. The dialog
  // stores customisations OVER the chart spec, so the defaults are that spec
  // with no stored customisation (DEFAULT_CHART_SETTINGS fills whatever the
  // spec leaves unset) — and a saved reset stores nothing for the panel.
  // Scoped to the selected panel; sibling panels keep their own settings.
  const handleReset = () => {
    const panel = flattenChartPanels(chartData).find(candidate => candidate.path === activeChartPath);
    if (!panel) return;
    const defaults = buildSettingsByPath(panel.chart, {}, panel.path);
    setSettingsByPath(prev => ({ ...prev, ...defaults }));
  };

  // Handle individual setting changes
  const handleSettingChange = (settingName, value) => {
    setSettingsByPath(prev => ({
      ...prev,
      [activeChartPath]: {
        ...(prev[activeChartPath] || DEFAULT_CHART_SETTINGS),
        [settingName]: value,
      },
    }));
  };

  const chartPanels = editablePanels(chartData);
  const activePanel = chartPanels.find(panel => panel.path === activeChartPath)
    || chartPanels[0];
  const settings = settingsByPath[activePanel?.path] || DEFAULT_CHART_SETTINGS;
  const activeChartType = activePanel?.chart?.type;
  const allowed = TYPE_SETTINGS[activeChartType] || [];

  return (
    <Dialog 
      open={open} 
      onClose={() => !saving && onClose(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 1,
          backgroundColor: '#1A1E26', 
          color: '#F0F2F5'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div" sx={{ color: '#4E85EB' }}>
            Chart Settings
          </Typography>
          <IconButton 
            onClick={() => onClose(false)} 
            disabled={saving}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" gutterBottom>{error}</Typography>
        ) : (
          <Box>
            {chartPanels.length > 1 && (
              <Tabs
                value={activePanel?.path || ROOT_CHART_PATH}
                onChange={(_, value) => setActiveChartPath(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                {chartPanels.map(panel => (
                  <Tab
                    key={panel.path}
                    value={panel.path}
                    label={panelLabel(panel, chartPanels.filter((other) => panelLabel(other) === panelLabel(panel)).length > 1)}
                  />
                ))}
              </Tabs>
            )}

            <Typography variant="subtitle1" gutterBottom sx={{ color: '#4E85EB', mt: 2 }}>
              Display Settings
            </Typography>
            <Box>
              {allowed.includes('grid') && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.show_grid}
                      onChange={(e) => handleSettingChange('show_grid', e.target.checked)}
                    />
                  }
                  label="Show Grid"
                />
              )}
              {allowed.includes('show_legend') && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.show_legend}
                      onChange={(e) => handleSettingChange('show_legend', e.target.checked)}
                    />
                  }
                  label="Show Legend"
                />
              )}
            </Box>

            {allowed.includes('x_axis_rotation') && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#4E85EB' }}>
                  Axis Settings
                </Typography>
                <Box mb={2}>
                  <Typography variant="body2" gutterBottom>
                    X-Axis Label Rotation: {settings.x_axis_rotation}°
                  </Typography>
                  <Slider
                    value={settings.x_axis_rotation}
                    onChange={(e, value) => handleSettingChange('x_axis_rotation', value)}
                    min={0}
                    max={90}
                    step={5}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </>
            )}

            {activeChartType === 'line' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#4E85EB' }}>
                  Line Chart Settings
                </Typography>
                <Box mb={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.show_points}
                        onChange={(e) => handleSettingChange('show_points', e.target.checked)}
                      />
                    }
                    label="Show Data Points"
                  />
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" gutterBottom>
                    Line Width: {settings.line_width}px
                  </Typography>
                  <Slider
                    value={settings.line_width}
                    onChange={(e, value) => handleSettingChange('line_width', value)}
                    min={1}
                    max={5}
                    step={0.5}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </>
            )}

            {activeChartType === 'bar' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#4E85EB' }}>
                  Bar Chart Settings
                </Typography>
                <Box mb={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.horizontal}
                        onChange={(e) => handleSettingChange('horizontal', e.target.checked)}
                      />
                    }
                    label="Horizontal Bars"
                  />
                </Box>
                <Box mb={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.stacked}
                        onChange={(e) => handleSettingChange('stacked', e.target.checked)}
                      />
                    }
                    label="Stacked"
                  />
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" gutterBottom>
                    Bar Width: {settings.bar_width}
                  </Typography>
                  <Slider
                    value={settings.bar_width}
                    onChange={(e, value) => handleSettingChange('bar_width', value)}
                    min={0.1}
                    max={1}
                    step={0.1}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      {saveError && (
        <Typography variant="body2" color="error.main" sx={{ px: 3, pt: 1.5 }}>
          {saveError}
        </Typography>
      )}
      <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
        <Button 
          onClick={handleReset} 
          color="primary" 
          disabled={loading || saving || !chartData}
        >
          Reset to Defaults
        </Button>
        <Box>
          <Button 
            onClick={() => onClose(false)} 
            sx={{ mr: 1 }}
            disabled={saving}
          >
            Cancel
          </Button>
          {/* Enabled only once THIS target's chart has loaded: a failed load
              leaves no chart, so nothing stale can be saved under this entry.
              A failed SAVE leaves the form on screen, so retrying is not blind. */}
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            disabled={loading || saving || !chartData || Boolean(error)}
            startIcon={saving && <CircularProgress size={20} color="inherit" />}
          >
            Save Settings
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default ChartSettings;
